import { batch } from 'solid-js'
import { applySnapshotValue, withSnapshotHydration } from '../../lib/snapshot-value.ts'
import * as catalog from './catalog.ts'
import * as customers from './customers.ts'
import * as foreignPayments from './foreign-payments.ts'
import * as invoices from './invoices.ts'
import * as machineLog from './machine-log.ts'
import * as orders from './orders.ts'
import * as people from './people.ts'
import * as pricing from './pricing.ts'
import * as production from './production.ts'
import * as shipments from './shipments.ts'
import * as stock from './stock-moves.ts'
import * as suppliers from './suppliers.ts'
import * as techCards from './tech-cards.ts'
import * as warehouse from './warehouse.ts'

const records: Record<string, unknown> = {
  ...catalog,
  ...foreignPayments,
  ...customers,
  ...invoices,
  ...machineLog,
  ...orders,
  ...people,
  ...pricing,
  ...production,
  ...shipments,
  ...stock,
  ...suppliers,
  ...techCards,
  ...warehouse,
}

const names = new Set(
  'FOREIGN_PAYMENTS PEOPLE STAGES SELLER_ENTITIES PRODUCTS ROUTE_TEMPLATE WORK_CENTERS ARCHIVED_WORK_CENTERS EQUIPMENT CURRENCIES VAT_MODES CUSTOMERS CUSTOMER_CATEGORIES PERSONS LINKS INVOICES REPAIR_LOG MACHINE_SESSIONS INTERNAL_ORDER_SEEDS MANUAL_PLAN_ROWS SCRAP_SEED SUPPLIERS SUPPLIER_GROUPS WAREHOUSES LOCATIONS ARCHIVED_LOCATIONS MATERIAL_CATEGORIES ARCHIVED_CATEGORIES UNITS MATERIALS BOMS EXPECTED_RECEIPTS SAMPLE_TTN ORDER_SEEDS SEED_ACTIVITY SEED_FILES CRM_TASKS PAYMENT_REQUESTS STOCK_MOVES SHIPMENTS OPERATIONS'.split(
    ' ',
  ),
)

export const hydrateData = (
  data: Record<string, unknown>,
  options: { replaceStaff?: boolean } = {},
) => {
  withSnapshotHydration(() =>
    batch(() => {
      for (const name of names) {
        const target = records[name]
        const value = data[name]
        if (Array.isArray(target)) {
          const incoming = Array.isArray(value) ? value : []
          // A slower SSE refetch must not undo a completed staff command.
          const latest =
            name === 'PEOPLE' && !options.replaceStaff
              ? incoming.map((person) => {
                  const previous = people.PEOPLE.find((row) => row.id === person.id)
                  return previous && (previous.version ?? 0) > (person.version ?? 0)
                    ? Object.fromEntries(
                        Object.keys(person).map((key) => [
                          key,
                          (previous as unknown as Record<string, unknown>)[key],
                        ]),
                      )
                    : person
                })
              : incoming
          if (name === 'PEOPLE' && !options.replaceStaff) {
            // Staff is disabled rather than deleted. A pre-creation response cannot remove a new row.
            // Keep only fields in this actor's current directory shape.
            const keys = Object.keys(incoming[0] ?? {})
            for (const previous of people.PEOPLE) {
              if (!latest.some((person) => person.id === previous.id))
                latest.push(
                  Object.fromEntries(
                    keys.map((key) => [key, (previous as unknown as Record<string, unknown>)[key]]),
                  ),
                )
            }
          }
          applySnapshotValue(target, latest)
        } else if (target && typeof target === 'object') {
          applySnapshotValue(
            target,
            value && typeof value === 'object' && !Array.isArray(value) ? value : {},
          )
        }
      }
    }),
  )
}
